import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { Billboard, OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import { Link } from 'react-router';
import { ArrowLeftIcon, CrosshairIcon, MapIcon, Maximize2Icon, MountainSnowIcon, Rotate3DIcon, TreesIcon } from 'lucide-react';
import { PedestrianControls } from '@/components/twin/pedestrian-controls';
import { PedestrianSimulation, createPedestrian, ALL_PEDESTRIAN_ROLES, type PedestrianMeta, type PedestrianRole, type PedestrianState, type WalkBounds } from '@/components/twin/pedestrian-simulation';
import { TwinAvatarCustomizer } from '@/components/twin/twin-avatar-customizer';
import { useLivePresencePedestrians } from '@/components/twin/use-live-presence-pedestrians';
import { useFullscreenTwin } from '@/hooks/use-fullscreen-twin';
import { useTwinPresence } from '@/hooks/use-twin-presence';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const TRAIL_MAP_URL = '/falls-creek-trail-map.png';
const TERRAIN_WIDTH = 62;
const TERRAIN_DEPTH = 48;
const TERRAIN_BASE_ELEVATION_METERS = 1500;
const TERRAIN_VERTICAL_SCALE = 0.036;
const FALLS_CREEK_WALK_BOUNDS: WalkBounds = { minX: -29, maxX: 29, minZ: -23, maxZ: 23 };
const FALLS_CREEK_GEO_ANCHOR = { latitude: -36.862, longitude: 147.279, elevationMeters: 1600 } as const;
const RESORT_METRES_PER_WORLD_UNIT = 18;
const RESORT_NORTH_ROTATION_RADIANS = 0;

interface ResortTerrainPoint {
  floorX: number;
  floorY: number;
  floorZ: number;
}

interface GpsCoordinate {
  latitude: number;
  longitude: number;
  elevationMeters: number;
}

function metresPerDegreeLatitude(latitude: number): number {
  const radians = latitude * (Math.PI / 180);
  return 111_132.92 - 559.82 * Math.cos(2 * radians) + 1.175 * Math.cos(4 * radians);
}

function metresPerDegreeLongitude(latitude: number): number {
  const radians = latitude * (Math.PI / 180);
  return 111_412.84 * Math.cos(radians) - 93.5 * Math.cos(3 * radians);
}

/** Converts WGS84 coordinates to the local, terrain-aligned Falls Creek scene. */
export function projectGpsToResortTerrain(latitude: number, longitude: number, elevationMeters?: number): ResortTerrainPoint {
  const metresEast = (longitude - FALLS_CREEK_GEO_ANCHOR.longitude) * metresPerDegreeLongitude(FALLS_CREEK_GEO_ANCHOR.latitude);
  const metresNorth = (latitude - FALLS_CREEK_GEO_ANCHOR.latitude) * metresPerDegreeLatitude(FALLS_CREEK_GEO_ANCHOR.latitude);
  const cosine = Math.cos(RESORT_NORTH_ROTATION_RADIANS);
  const sine = Math.sin(RESORT_NORTH_ROTATION_RADIANS);
  const floorX = (metresEast * cosine + metresNorth * sine) / RESORT_METRES_PER_WORLD_UNIT;
  const floorZ = (metresEast * sine - metresNorth * cosine) / RESORT_METRES_PER_WORLD_UNIT;

  return {
    floorX,
    floorZ,
    floorY: elevationMeters == null
      ? terrainElevation(floorX, floorZ)
      : 0.18 + (elevationMeters - TERRAIN_BASE_ELEVATION_METERS) * TERRAIN_VERTICAL_SCALE,
  };
}

/** Converts a local Falls Creek terrain point back to WGS84 with its ASL elevation. */
export function projectResortTerrainToGps(floorX: number, floorZ: number, elevationMeters = terrainElevationMeters(floorX, floorZ)): GpsCoordinate {
  const cosine = Math.cos(RESORT_NORTH_ROTATION_RADIANS);
  const sine = Math.sin(RESORT_NORTH_ROTATION_RADIANS);
  const metresEast = RESORT_METRES_PER_WORLD_UNIT * (floorX * cosine + floorZ * sine);
  const metresNorth = RESORT_METRES_PER_WORLD_UNIT * (floorX * sine - floorZ * cosine);

  return {
    latitude: FALLS_CREEK_GEO_ANCHOR.latitude + metresNorth / metresPerDegreeLatitude(FALLS_CREEK_GEO_ANCHOR.latitude),
    longitude: FALLS_CREEK_GEO_ANCHOR.longitude + metresEast / metresPerDegreeLongitude(FALLS_CREEK_GEO_ANCHOR.latitude),
    elevationMeters,
  };
}

const bell = (x: number, z: number, centerX: number, centerZ: number, radiusX: number, radiusZ: number) => (
  Math.exp(-(((x - centerX) / radiusX) ** 2 + ((z - centerZ) / radiusZ) ** 2))
);

// Elevation is modelled in ASL metres, then scaled into the 3D world. Keeping
// this as the single terrain source ensures infrastructure remains grounded as
// the heightmap and future GPS projection evolve together.
function terrainElevationMeters(x: number, z: number): number {
  const mountainMass = bell(x, z, -8, -2, 29, 22) * 42;
  const mtMcKaySummit = bell(x, z, -23.5, -12.5, 7.8, 6.2) * 142;
  const scottsRidge = bell(x, z, -18.2, 1.6, 7.4, 7.1) * 118;
  const summitRidge = bell(x, z, -16.7, -5.1, 8.6, 5.7) * 92;
  const internationalBowl = bell(x, z, -7.1, -6.4, 8.8, 6.4) * 76;
  const gullyRidge = bell(x, z, 2.6, -8.8, 7.2, 5.6) * 68;
  const alpineShoulder = bell(x, z, -8.8, -1.7, 15.5, 11.8) * 33;
  const villageGully = bell(x, z, 8.5, -3.8, 7.8, 6.8) * 47;
  const villageBowl = bell(x, z, 11.5, 7.8, 8.2, 7.1) * 32;
  const slalomCarparkTerrace = bell(x, z, 10.8, 15.1, 8.4, 4.5) * 26;
  const windyCornerPlateau = bell(x, z, 23.5, 6.6, 7.2, 6.4) * 43;
  const villageDrainage = bell(x, z, 4.7, 4.4, 14.5, 4.1) * 38;
  const contourTexture = Math.sin(x * 0.52 + z * 0.16) * 2.1 + Math.cos(z * 0.67 - x * 0.11) * 1.5;

  return THREE.MathUtils.clamp(
    TERRAIN_BASE_ELEVATION_METERS + mountainMass + mtMcKaySummit + scottsRidge + summitRidge
      + internationalBowl + gullyRidge + alpineShoulder + slalomCarparkTerrace + windyCornerPlateau
      - villageGully - villageBowl - villageDrainage + contourTexture,
    TERRAIN_BASE_ELEVATION_METERS,
    1780,
  );
}

function terrainElevation(x: number, z: number): number {
  return 0.18 + (terrainElevationMeters(x, z) - TERRAIN_BASE_ELEVATION_METERS) * TERRAIN_VERTICAL_SCALE;
}

const liftPoint = (x: number, z: number): [number, number, number] => [x, terrainElevation(x, z), z];

// Coordinates are projected from the red lift corridors on the supplied trail map,
// which is draped across the extended 62 × 48 terrain plane below. Each path runs uphill.
const LIFTS = [
  { id: 'rocky-knolls', name: 'Rocky Knolls', start: liftPoint(-16.1, 8.9), end: liftPoint(-18.6, 4.5), color: '#e11d48' },
  { id: 'scott', name: 'Scotts', start: liftPoint(-15.0, 4.5), end: liftPoint(-18.9, 1.3), color: '#e11d48' },
  { id: 'eagle', name: 'Eagle Express', start: liftPoint(-9.8, 3.1), end: liftPoint(-12.1, -0.4), color: '#e11d48' },
  { id: 'summit', name: 'Summit', start: liftPoint(-11.9, -3.1), end: liftPoint(-17.0, -5.2), color: '#e11d48' },
  { id: 'international', name: 'International', start: liftPoint(-2.2, -4.9), end: liftPoint(-7.2, -6.5), color: '#e11d48' },
  { id: 'gully', name: 'Gully', start: liftPoint(9.0, -4.2), end: liftPoint(2.5, -8.7), color: '#e11d48' },
] as const;

const RUNS = [
  { name: 'Wombat’s Ramble', position: [-11.6, 0.34, -0.3] as [number, number, number], rotation: 0.17, width: 2.4, length: 17 },
  { name: 'Highway 83', position: [-7.5, 0.42, 2.1] as [number, number, number], rotation: 0.39, width: 2.1, length: 15 },
  { name: 'Ruined Castle', position: [-4.1, 0.48, -1.1] as [number, number, number], rotation: -0.19, width: 2.2, length: 15 },
  { name: 'Eagle Run', position: [2.2, 0.48, -0.5] as [number, number, number], rotation: -0.28, width: 2.3, length: 19 },
  { name: 'Summit', position: [7.7, 0.52, -0.4] as [number, number, number], rotation: -0.2, width: 2.2, length: 19 },
  { name: 'International', position: [12.4, 0.45, 0.2] as [number, number, number], rotation: -0.18, width: 2, length: 17 },
] as const;

type LiftDefinition = (typeof LIFTS)[number];

type ZoomTarget = [number, number, number];

function liftFocusTarget(lift: LiftDefinition): ZoomTarget {
  const progress = 0.52;
  const x = THREE.MathUtils.lerp(lift.start[0], lift.end[0], progress);
  const z = THREE.MathUtils.lerp(lift.start[2], lift.end[2], progress);

  return [x, terrainElevation(x, z) + 2.4, z];
}

function createTerrainGeometry(): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(TERRAIN_WIDTH, TERRAIN_DEPTH, 160, 128);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;

  for (let index = 0; index < positions.count; index += 1) {
    positions.setZ(index, terrainElevation(positions.getX(index), positions.getY(index)));
  }

  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

function SnowTerrain({ onInspect }: { onInspect: (point: THREE.Vector3) => void }) {
  const geometry = useMemo(createTerrainGeometry, []);
  const trailMap = useLoader(THREE.TextureLoader, TRAIL_MAP_URL);

  useEffect(() => {
    trailMap.colorSpace = THREE.SRGBColorSpace;
    trailMap.anisotropy = 8;
    trailMap.needsUpdate = true;
  }, [trailMap]);

  return <mesh
    geometry={geometry}
    rotation={[-Math.PI / 2, 0, 0]}
    receiveShadow
    onPointerMove={(event) => onInspect(event.point.clone())}
    onClick={(event) => onInspect(event.point.clone())}
  >
    <meshStandardMaterial map={trailMap} color="#e1eff5" roughness={0.96} metalness={0} />
  </mesh>;
}

function PineTree({ x, z, scale = 1 }: { x: number; z: number; scale?: number }) {
  return <group position={[x, terrainElevation(x, z), z]} scale={scale}>
    <mesh position={[0, 0.65, 0]} castShadow>
      <cylinderGeometry args={[0.12, 0.17, 1.3, 8]} />
      <meshStandardMaterial color="#654321" />
    </mesh>
    {[0.65, 1.06, 1.43].map((height, index) => (
      <mesh key={height} position={[0, height, 0]} castShadow>
        <coneGeometry args={[0.7 - index * 0.13, 1.05, 8]} />
        <meshStandardMaterial color="#174d3b" />
      </mesh>
    ))}
  </group>;
}

function SkiRun({ position, rotation, width, length, name }: typeof RUNS[number]) {
  return <group position={[position[0], terrainElevation(position[0], position[2]) + 0.025, position[2]]} rotation={[-Math.PI / 2, 0, rotation]}>
    <mesh receiveShadow>
      <planeGeometry args={[width, length]} />
      <meshStandardMaterial color="#f8fdff" transparent opacity={0.55} roughness={0.9} />
    </mesh>
    <Text position={[0, 0.03, -length * 0.22]} rotation={[0, 0, 0]} fontSize={0.31} color="#17334a" anchorX="center">
      {name}
    </Text>
  </group>;
}

function LiftChair({ lift, offset, selected }: { lift: LiftDefinition; offset: number; selected: boolean }) {
  const chairRef = useRef<THREE.Group>(null);

  useFrame(({ clock }) => {
    const progress = (clock.getElapsedTime() * 0.075 + offset) % 1;
    const travel = progress < 0.5 ? progress * 2 : 2 - progress * 2;
    const direction = progress < 0.5 ? 1 : -1;
    const x = THREE.MathUtils.lerp(lift.start[0], lift.end[0], travel);
    const y = THREE.MathUtils.lerp(lift.start[1] + 2.42, lift.end[1] + 2.42, travel);
    const z = THREE.MathUtils.lerp(lift.start[2], lift.end[2], travel);
    chairRef.current?.position.set(x, y, z);
    if (chairRef.current) {
      chairRef.current.rotation.y = Math.atan2(lift.end[0] - lift.start[0], lift.end[2] - lift.start[2]) + (direction < 0 ? Math.PI : 0);
    }
  });

  return <group ref={chairRef}>
    <mesh position={[0, -0.48, 0]}><cylinderGeometry args={[0.028, 0.028, 0.96, 6]} /><meshStandardMaterial color="#3f4b56" /></mesh>
    <mesh position={[0, -0.94, 0]} castShadow><boxGeometry args={[0.72, 0.13, 0.28]} /><meshStandardMaterial color={selected ? '#facc15' : lift.color} /></mesh>
    <mesh position={[0, -0.76, 0.12]}><boxGeometry args={[0.72, 0.42, 0.06]} /><meshStandardMaterial color="#374151" /></mesh>
  </group>;
}

function SkiLift({ lift, selected }: { lift: LiftDefinition; selected: boolean }) {
  const cableStart = new THREE.Vector3(lift.start[0], lift.start[1] + 2.42, lift.start[2]);
  const cableEnd = new THREE.Vector3(lift.end[0], lift.end[1] + 2.42, lift.end[2]);
  const vector = cableEnd.clone().sub(cableStart);
  const distance = vector.length();
  const midpoint = cableStart.clone().add(cableEnd).multiplyScalar(0.5);
  const cableRotation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), vector.normalize());
  const supportPoints = [0.25, 0.5, 0.75].map((offset) => {
    const x = THREE.MathUtils.lerp(lift.start[0], lift.end[0], offset);
    const z = THREE.MathUtils.lerp(lift.start[2], lift.end[2], offset);
    return [x, terrainElevation(x, z), z] as [number, number, number];
  });

  return <group>
    {[lift.start, ...supportPoints, lift.end].map((point) => (
      <group key={point.join('-')} position={point}>
        <mesh position={[0, 1.25, 0]} castShadow><cylinderGeometry args={[0.12, 0.18, 2.5, 10]} /><meshStandardMaterial color={selected ? '#facc15' : '#53636f'} /></mesh>
        <mesh position={[0, 2.42, 0]}><boxGeometry args={[1.05, 0.13, 0.18]} /><meshStandardMaterial color={selected ? '#facc15' : lift.color} /></mesh>
      </group>
    ))}
    <mesh position={midpoint} quaternion={cableRotation}><cylinderGeometry args={[0.026, 0.026, distance, 8]} /><meshStandardMaterial color="#25333d" /></mesh>
    {[0.06, 0.22, 0.38, 0.54, 0.7, 0.86].map((offset) => <LiftChair key={offset} lift={lift} offset={offset} selected={selected} />)}
    <Billboard position={liftFocusTarget(lift)}>
      <Text
        fontSize={0.4}
        color={selected ? '#facc15' : '#e11d48'}
        outlineWidth={0.012}
        outlineColor="#fff7ed"
        anchorX="center"
        anchorY="middle"
      >
        {lift.name}
      </Text>
    </Billboard>
  </group>;
}

function VillageBuilding({ x, z, width, depth, name, roof = '#263f55' }: { x: number; z: number; width: number; depth: number; name: string; roof?: string }) {
  return <group position={[x, terrainElevation(x, z), z]}>
    <mesh position={[0, 0.7, 0]} castShadow receiveShadow><boxGeometry args={[width, 1.4, depth]} /><meshStandardMaterial color="#b8835d" /></mesh>
    <mesh position={[0, 1.52, 0]} rotation={[0, Math.PI / 4, 0]} castShadow><coneGeometry args={[Math.max(width, depth) * 0.76, 0.7, 4]} /><meshStandardMaterial color={roof} /></mesh>
    <Text position={[0, 2.35, 0]} fontSize={0.31} color="#17334a" anchorX="center">{name}</Text>
  </group>;
}

interface ResortLandmark {
  id: 'windy-corner' | 'lower-slalom';
  name: string;
  x: number;
  z: number;
  description: string;
  features: readonly string[];
}

const WINDY_CORNER_LANDMARK: ResortLandmark = {
  id: 'windy-corner',
  name: 'Windy Corner',
  x: 23.5,
  z: 6.6,
  description: 'Eastern day-use arrival zone with the Nordic trailhead, day shelter, and snow parking.',
  features: ['Day shelter', 'Nordic trailhead', 'Parking bays'],
};
const LOWER_SLALOM_LANDMARK: ResortLandmark = {
  id: 'lower-slalom',
  name: 'Lower Slalom Carpark',
  x: 10.8,
  z: 15.1,
  description: 'North-west village entry with the transit hub, visitor drop-off, and lower parking terrace.',
  features: ['Transit hub', 'Village entry', 'Parking bays'],
};

const RESORT_CAMERA_PRESETS = [
  { id: 'village-bowl', label: 'Village Bowl', target: [4, 12] as const },
  { id: 'lower-slalom', label: 'Slalom Carpark', landmark: LOWER_SLALOM_LANDMARK },
  { id: 'windy-corner', label: 'Windy Corner', landmark: WINDY_CORNER_LANDMARK },
  { id: 'summit', label: 'Summit', lift: LIFTS[3] },
] as const;

type ResortCameraPreset = (typeof RESORT_CAMERA_PRESETS)[number];

function ResortZoneLabel({ name, position, selected }: { name: string; position: [number, number, number]; selected: boolean }) {
  return <Billboard position={position}>
    <Text fontSize={0.42} color={selected ? '#facc15' : '#123a54'} outlineWidth={0.014} outlineColor="#f8fdff" anchorX="center" anchorY="middle">
      {name}
    </Text>
  </Billboard>;
}

function WindyCornerZone({ selected, onSelect }: { selected: boolean; onSelect: () => void }) {
  const landmark = WINDY_CORNER_LANDMARK;
  const ground = terrainElevation(landmark.x, landmark.z);
  const parkingBays = [-2.3, -1.15, 0, 1.15, 2.3] as const;

  return <group position={[landmark.x, ground, landmark.z]} onClick={(event) => { event.stopPropagation(); onSelect(); }}>
    <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[8.8, 6.4]} /><meshStandardMaterial color="#66727a" roughness={0.9} /></mesh>
    <mesh position={[-2.4, 0.72, -0.65]} castShadow receiveShadow><boxGeometry args={[3.5, 1.4, 2.7]} /><meshStandardMaterial color="#b8835d" /></mesh>
    <mesh position={[-2.4, 1.62, -0.65]} rotation={[0, Math.PI / 4, 0]} castShadow><coneGeometry args={[2.65, 0.75, 4]} /><meshStandardMaterial color="#3c5d78" /></mesh>
    <mesh position={[2.7, 0.76, 1.35]} castShadow><boxGeometry args={[0.18, 1.52, 0.18]} /><meshStandardMaterial color="#43545f" /></mesh>
    <mesh position={[2.7, 1.62, 1.35]} castShadow><boxGeometry args={[1.35, 0.54, 0.1]} /><meshStandardMaterial color="#1d6d85" /></mesh>
    <Text position={[2.7, 1.62, 1.42]} rotation={[0, Math.PI, 0]} fontSize={0.18} color="#ffffff" anchorX="center" anchorY="middle">NORDIC</Text>
    {parkingBays.map((offset) => <group key={offset} position={[offset, 0.055, 2.45]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[0.82, 1.55]} /><meshStandardMaterial color="#d9e3e8" /></mesh>
      <mesh position={[0, 0.15, 0]} castShadow><boxGeometry args={[0.58, 0.24, 1.02]} /><meshStandardMaterial color={selected ? '#facc15' : '#496a7a'} /></mesh>
    </group>)}
    <ResortZoneLabel name="Windy Corner · Day Shelter & Nordic Entry" position={[0, 3.1, 0]} selected={selected} />
  </group>;
}

function LowerSlalomZone({ selected, onSelect }: { selected: boolean; onSelect: () => void }) {
  const landmark = LOWER_SLALOM_LANDMARK;
  const ground = terrainElevation(landmark.x, landmark.z);
  const parkingBays = [-3.05, -1.85, -0.65, 0.65, 1.85, 3.05] as const;

  return <group position={[landmark.x, ground, landmark.z]} onClick={(event) => { event.stopPropagation(); onSelect(); }}>
    <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[11.4, 7.2]} /><meshStandardMaterial color="#59666e" roughness={0.9} /></mesh>
    <mesh position={[-3.55, 0.78, -1.35]} castShadow receiveShadow><boxGeometry args={[2.45, 1.55, 3.1]} /><meshStandardMaterial color="#c18b63" /></mesh>
    <mesh position={[-3.55, 1.72, -1.35]} rotation={[0, Math.PI / 4, 0]} castShadow><coneGeometry args={[2.35, 0.66, 4]} /><meshStandardMaterial color="#b84f43" /></mesh>
    <mesh position={[2.8, 0.58, -1.6]} castShadow><boxGeometry args={[3.15, 1.15, 1.3]} /><meshStandardMaterial color="#d6e3e8" /></mesh>
    <mesh position={[2.8, 1.26, -1.6]} castShadow><boxGeometry args={[3.35, 0.26, 1.48]} /><meshStandardMaterial color="#386b86" /></mesh>
    <Text position={[2.8, 1.58, -1.6]} rotation={[0, Math.PI, 0]} fontSize={0.21} color="#123a54" anchorX="center">BUS TERMINAL</Text>
    {parkingBays.map((offset) => <group key={offset} position={[offset, 0.055, 2.2]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[0.88, 1.65]} /><meshStandardMaterial color="#d9e3e8" /></mesh>
      <mesh position={[0, 0.15, 0]} castShadow><boxGeometry args={[0.62, 0.24, 1.07]} /><meshStandardMaterial color={selected ? '#facc15' : '#536f7c'} /></mesh>
    </group>)}
    <mesh position={[0, 0.16, -3.05]} castShadow><boxGeometry args={[8.8, 0.18, 0.38]} /><meshStandardMaterial color="#f5c84c" /></mesh>
    <ResortZoneLabel name="Lower Slalom · Transit Hub & Village Entry" position={[0, 3.05, 0]} selected={selected} />
  </group>;
}

function ClickToZoom({ onZoom, onInspect }: { onZoom: (point: THREE.Vector3) => void; onInspect: (point: THREE.Vector3) => void }) {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const point = new THREE.Vector3();
    let lastTouchTap: { time: number; x: number; y: number } | null = null;

    const zoomFromEvent = (event: MouseEvent | PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
        -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      if (raycaster.ray.intersectPlane(floor, point)) {
        onInspect(point.clone());
        point.y = terrainElevation(point.x, point.z) + 1.4;
        onZoom(point.clone());
      }
    };

    const handleDoubleClick = (event: MouseEvent) => {
      event.preventDefault();
      zoomFromEvent(event);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') return;
      const now = event.timeStamp;
      const isDoubleTap = lastTouchTap && now - lastTouchTap.time < 350 &&
        Math.hypot(event.clientX - lastTouchTap.x, event.clientY - lastTouchTap.y) < 24;
      lastTouchTap = { time: now, x: event.clientX, y: event.clientY };
      if (isDoubleTap) {
        zoomFromEvent(event);
        lastTouchTap = null;
      }
    };

    canvas.addEventListener('dblclick', handleDoubleClick);
    canvas.addEventListener('pointerdown', handlePointerDown);
    return () => {
      canvas.removeEventListener('dblclick', handleDoubleClick);
      canvas.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [camera, gl, onInspect, onZoom]);

  return null;
}

type OrbitControlsHandle = {
  object: THREE.Camera & { position: THREE.Vector3 };
  target: THREE.Vector3;
  update: () => void;
  enabled: boolean;
};

function GpsHud({ coordinate }: { coordinate: GpsCoordinate }) {
  return <Card className="pointer-events-none w-64 border-white/60 bg-slate-950/90 text-white shadow-lg backdrop-blur">
    <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm text-white"><CrosshairIcon className="size-4 text-sky-300" />Live GPS coordinate</CardTitle></CardHeader>
    <CardContent className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs tabular-nums">
      <span className="text-slate-300">Latitude</span><span>{coordinate.latitude.toFixed(6)}°</span>
      <span className="text-slate-300">Longitude</span><span>{coordinate.longitude.toFixed(6)}°</span>
      <span className="text-slate-300">Elevation</span><span>{Math.round(coordinate.elevationMeters).toLocaleString()} m ASL</span>
    </CardContent>
  </Card>;
}

function ResortLocationDetails({ landmark }: { landmark: ResortLandmark }) {
  const coordinate = projectResortTerrainToGps(landmark.x, landmark.z);

  return <div className="space-y-3 text-sm">
    <p className="text-muted-foreground">{landmark.description}</p>
    <div className="flex flex-wrap gap-1.5">
      {landmark.features.map((feature) => <Badge key={feature} variant="secondary">{feature}</Badge>)}
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs tabular-nums text-muted-foreground">
      <span>Latitude</span><span>{coordinate.latitude.toFixed(6)}°</span>
      <span>Longitude</span><span>{coordinate.longitude.toFixed(6)}°</span>
      <span>Terrain</span><span>{Math.round(coordinate.elevationMeters).toLocaleString()} m ASL</span>
    </div>
  </div>;
}

function ResortCameraController({ zoomTarget }: { zoomTarget: ZoomTarget | null }) {
  const controlsRef = useRef<OrbitControlsHandle | null>(null);
  const fromPosition = useRef(new THREE.Vector3());
  const fromTarget = useRef(new THREE.Vector3());
  const toPosition = useRef(new THREE.Vector3());
  const toTarget = useRef(new THREE.Vector3());
  const progress = useRef(1);
  const animating = useRef(false);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || !zoomTarget) return;

    fromPosition.current.copy(controls.object.position);
    fromTarget.current.copy(controls.target);
    toTarget.current.set(
      THREE.MathUtils.clamp(zoomTarget[0], -21, 21),
      THREE.MathUtils.clamp(zoomTarget[1], 0, 14),
      THREE.MathUtils.clamp(zoomTarget[2], -15, 15),
    );
    const offset = controls.object.position.clone().sub(controls.target);
    const distance = THREE.MathUtils.clamp(offset.length() * 0.65, 16, 47);
    toPosition.current.copy(toTarget.current).addScaledVector(offset.normalize(), distance);
    progress.current = 0;
    animating.current = true;
    controls.enabled = false;
  }, [zoomTarget]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls || !animating.current) return;

    progress.current = Math.min(1, progress.current + delta * 2.5);
    const eased = 1 - Math.pow(1 - progress.current, 3);
    controls.object.position.lerpVectors(fromPosition.current, toPosition.current, eased);
    controls.target.lerpVectors(fromTarget.current, toTarget.current, eased);
    controls.update();

    if (progress.current === 1) {
      animating.current = false;
      controls.enabled = true;
    }
  });

  return <OrbitControls ref={controlsRef as never} makeDefault enableDamping dampingFactor={0.08} minDistance={16} maxDistance={47} maxPolarAngle={Math.PI / 2.12} target={[0, 3, 0]} />;
}

function ResortScene({
  activeLift,
  zoomTarget,
  pedestrians,
  livePedestrians,
  pedestrianSpeed,
  pedestrianRoles,
  showPaths,
  selectedPedestrianId,
  onSelectPedestrian,
  onInspectTerrain,
  selectedLandmarkId,
  onSelectLandmark,
}: {
  activeLift: string | null;
  zoomTarget: ZoomTarget | null;
  pedestrians: PedestrianState[];
  livePedestrians: PedestrianState[];
  pedestrianSpeed: number;
  pedestrianRoles: readonly PedestrianRole[];
  showPaths: boolean;
  selectedPedestrianId: string | null;
  onSelectPedestrian: (pedestrian: PedestrianMeta) => void;
  onInspectTerrain: (point: THREE.Vector3) => void;
  selectedLandmarkId: ResortLandmark['id'] | null;
  onSelectLandmark: (landmark: ResortLandmark) => void;
}) {
  const treeLocations = useMemo(() => [
    [-18, -8], [-17, -3], [-18, 2], [-15, 6], [-13, -10], [-10, -12], [-7, 5], [-1, -12], [5, -10], [11, -7], [18, -6], [18, 0], [16, 5], [12, 10], [5, 12], [-5, 11],
  ] as const, []);

  return <>
    <color attach="background" args={['#8ac9e9']} />
    <fog attach="fog" args={['#8ac9e9', 29, 65]} />
    <ambientLight intensity={1.5} />
    <directionalLight position={[10, 22, 7]} intensity={2.1} castShadow shadow-mapSize={[1024, 1024]} />
    <SnowTerrain onInspect={onInspectTerrain} />
    {RUNS.map((run) => <SkiRun key={run.name} {...run} />)}
    {treeLocations.map(([x, z]) => <PineTree key={`${x}-${z}`} x={x} z={z} scale={0.75 + Math.abs(x % 3) * 0.1} />)}
    <VillageBuilding x={-8} z={12} width={3.7} depth={2.8} name="Alpine Woodsmoke" roof="#3c5d78" />
    <VillageBuilding x={-2.5} z={12} width={3.2} depth={2.5} name="Falls Creek Sports Complex" roof="#b84f43" />
    <VillageBuilding x={4} z={12} width={3.2} depth={2.6} name="Village Bowl" roof="#486b47" />
    <VillageBuilding x={9.5} z={11.4} width={4.1} depth={2.9} name="GEB’s" roof="#4e5968" />
    <WindyCornerZone selected={selectedLandmarkId === 'windy-corner'} onSelect={() => onSelectLandmark(WINDY_CORNER_LANDMARK)} />
    <LowerSlalomZone selected={selectedLandmarkId === 'lower-slalom'} onSelect={() => onSelectLandmark(LOWER_SLALOM_LANDMARK)} />
    {LIFTS.map((lift) => <SkiLift key={lift.id} lift={lift} selected={activeLift === lift.id} />)}
    <PedestrianSimulation
      pedestrians={[...pedestrians, ...livePedestrians]}
      bounds={FALLS_CREEK_WALK_BOUNDS}
      speedMultiplier={pedestrianSpeed}
      visibleRoles={pedestrianRoles}
      showPaths={showPaths}
      selectedId={selectedPedestrianId}
      onSelect={onSelectPedestrian}
      groundElevation={terrainElevation}
    />
    <Text position={[0, 16, -10]} fontSize={1.05} color="#154d6e" anchorX="center">Falls Creek Alpine Resort</Text>
    <ResortCameraController zoomTarget={zoomTarget} />
  </>;
}

export function FallsCreekTwinPage() {
  const [activeLift, setActiveLift] = useState<string | null>(null);
  const [selectedLandmarkId, setSelectedLandmarkId] = useState<ResortLandmark['id'] | null>(null);
  const [zoomTarget, setZoomTarget] = useState<ZoomTarget | null>(null);
  const [pedestriansEnabled, setPedestriansEnabled] = useState(true);
  const [pedestrianSpeed, setPedestrianSpeed] = useState(1);
  const [showPedestrianPaths, setShowPedestrianPaths] = useState(false);
  const [visiblePedestrianRoles, setVisiblePedestrianRoles] = useState<PedestrianRole[]>(() => [...ALL_PEDESTRIAN_ROLES]);
  const [pedestrianCount, setPedestrianCount] = useState(0);
  const [selectedPedestrian, setSelectedPedestrian] = useState<PedestrianMeta | null>(null);
  const [inspectedCoordinate, setInspectedCoordinate] = useState<GpsCoordinate>(() => projectResortTerrainToGps(0, 0));
  const [isMobile, setIsMobile] = useState(false);
  const pedestriansRef = useRef<PedestrianState[]>([]);
  const fullscreen = useFullscreenTwin();
  const presence = useTwinPresence({
    sceneId: 'resort:falls-creek',
    projectLocation: (location) => ({
      ...projectGpsToResortTerrain(location.latitude, location.longitude),
      accuracyMeters: location.accuracyMeters,
    }),
  });

  const projectResortPresence = useCallback((participant: { latitude?: number | null; longitude?: number | null; floorX?: number | null; floorZ?: number | null }) => {
    // GPS is authoritative so all participants use the current shared WGS84
    // calibration. Stored floor coordinates remain a fallback for older fixes.
    if (participant.latitude != null && participant.longitude != null) {
      const point = projectGpsToResortTerrain(participant.latitude, participant.longitude);
      return new THREE.Vector3(point.floorX, terrainElevation(point.floorX, point.floorZ), point.floorZ);
    }
    if (participant.floorX != null && participant.floorZ != null) {
      return new THREE.Vector3(participant.floorX, terrainElevation(participant.floorX, participant.floorZ), participant.floorZ);
    }
    return null;
  }, []);
  const ownLivePresence = useMemo(() => {
    if (!presence.isSharing || !presence.locationTrack.location) return null;
    const location = presence.locationTrack.location;
    const point = projectGpsToResortTerrain(location.latitude, location.longitude);
    return {
      userId: 'local:resort:falls-creek',
      displayName: presence.displayName,
      avatarColor: presence.avatarColor,
      accuracyMeters: location.accuracyMeters,
      position: new THREE.Vector3(point.floorX, terrainElevation(point.floorX, point.floorZ), point.floorZ),
    };
  }, [presence.avatarColor, presence.displayName, presence.isSharing, presence.locationTrack.location]);
  const livePedestrians = useLivePresencePedestrians(presence.participants, projectResortPresence, ownLivePresence);
  const scenePedestrians = useMemo(
    () => pedestriansEnabled ? [...pedestriansRef.current, ...livePedestrians] : [],
    [livePedestrians, pedestriansEnabled, pedestrianCount],
  );

  useEffect(() => {
    if (selectedPedestrian && !visiblePedestrianRoles.includes(selectedPedestrian.role)) setSelectedPedestrian(null);
  }, [selectedPedestrian, visiblePedestrianRoles]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  const inspectTerrainPoint = useCallback((point: THREE.Vector3) => {
    setInspectedCoordinate(projectResortTerrainToGps(point.x, point.z));
  }, []);
  const zoomTowardPoint = useCallback((point: THREE.Vector3) => {
    inspectTerrainPoint(point);
    setZoomTarget([point.x, point.y, point.z]);
  }, [inspectTerrainPoint]);
  const focusLift = useCallback((lift: LiftDefinition) => {
    const target = liftFocusTarget(lift);
    setActiveLift(lift.id);
    setSelectedLandmarkId(null);
    setInspectedCoordinate(projectResortTerrainToGps(target[0], target[2]));
    setZoomTarget(target);
  }, []);
  const focusLandmark = useCallback((landmark: ResortLandmark) => {
    const ground = terrainElevation(landmark.x, landmark.z);
    setActiveLift(null);
    setSelectedLandmarkId(landmark.id);
    setInspectedCoordinate(projectResortTerrainToGps(landmark.x, landmark.z));
    setZoomTarget([landmark.x, ground + 2, landmark.z]);
  }, []);
  const focusPreset = useCallback((preset: ResortCameraPreset) => {
    if ('landmark' in preset) {
      focusLandmark(preset.landmark);
      return;
    }
    if ('lift' in preset) {
      focusLift(preset.lift);
      return;
    }
    const [x, z] = preset.target;
    setActiveLift(null);
    setSelectedLandmarkId(null);
    setInspectedCoordinate(projectResortTerrainToGps(x, z));
    setZoomTarget([x, terrainElevation(x, z) + 2, z]);
  }, [focusLandmark, focusLift]);
  const handleSpawnPedestrian = useCallback(() => {
    pedestriansRef.current = [...pedestriansRef.current, createPedestrian(FALLS_CREEK_WALK_BOUNDS)];
    setPedestrianCount(pedestriansRef.current.length);
  }, []);
  const handleResetPedestrians = useCallback(() => {
    pedestriansRef.current = [];
    setPedestrianCount(0);
    setSelectedPedestrian(null);
  }, []);

  const selectedLandmark = selectedLandmarkId === 'windy-corner'
    ? WINDY_CORNER_LANDMARK
    : selectedLandmarkId === 'lower-slalom'
      ? LOWER_SLALOM_LANDMARK
      : null;

  const containerClassName = fullscreen.isFallbackFullscreen
    ? 'fixed inset-0 z-50 h-[100dvh] w-screen overflow-hidden bg-sky-100'
    : fullscreen.isFullscreen
      ? 'relative h-full w-full overflow-hidden bg-sky-100'
      : 'relative h-[calc(100vh-4rem)] min-h-[620px] overflow-hidden bg-sky-100';

  return <div ref={fullscreen.containerRef} className={containerClassName}>
    <Canvas camera={{ position: [23, 22, 28], fov: 48 }} shadows>
      <ClickToZoom onZoom={zoomTowardPoint} onInspect={inspectTerrainPoint} />
      <ResortScene
        activeLift={activeLift}
        zoomTarget={zoomTarget}
        pedestrians={scenePedestrians}
        livePedestrians={[]}
        pedestrianSpeed={pedestrianSpeed}
        pedestrianRoles={visiblePedestrianRoles}
        showPaths={showPedestrianPaths}
        selectedPedestrianId={selectedPedestrian?.id ?? null}
        onSelectPedestrian={setSelectedPedestrian}
        onInspectTerrain={inspectTerrainPoint}
        selectedLandmarkId={selectedLandmarkId}
        onSelectLandmark={focusLandmark}
      />
    </Canvas>

    <header className="pointer-events-auto absolute inset-x-0 top-0 z-20 flex flex-wrap items-center gap-3 border-b border-border bg-background/95 px-4 py-2 backdrop-blur">
      <Button asChild variant="ghost" size="icon" aria-label="Return to digital twins"><Link to="/twin"><ArrowLeftIcon /></Link></Button>
      <div className="flex items-center gap-2"><MountainSnowIcon className="size-5 text-sky-700" /><span className="font-semibold">Falls Creek Resort — 3D Alpine Twin</span><span className="hidden text-sm text-muted-foreground sm:inline">6 lifts · live terrain</span></div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => { setActiveLift(null); setSelectedLandmarkId(null); setInspectedCoordinate(projectResortTerrainToGps(0, 0)); setZoomTarget([0, 4, 0]); }}>Full Resort</Button>
        {RESORT_CAMERA_PRESETS.map((preset) => <Button
          key={preset.id}
          type="button"
          size="sm"
          variant={selectedLandmarkId === preset.id || (preset.id === 'summit' && activeLift === 'summit') ? 'secondary' : 'outline'}
          onClick={() => focusPreset(preset)}
        >
          {preset.label}
        </Button>)}
        <TwinAvatarCustomizer sceneLabel="Falls Creek Resort" presence={presence} />
        <Button type="button" size="icon" variant="outline" onClick={() => { void fullscreen.toggleFullscreen(); }} aria-label="Toggle fullscreen"><Maximize2Icon /></Button>
      </div>
    </header>

    <div className="pointer-events-none absolute inset-x-0 top-16 flex items-start justify-between gap-4 p-4 sm:p-6">
      <Card className="pointer-events-auto hidden max-w-sm border-white/60 bg-white/92 shadow-lg backdrop-blur lg:block">
        <CardHeader className="space-y-1 pb-3"><CardTitle className="text-xl">Falls Creek Resort</CardTitle><p className="text-sm text-muted-foreground">Mapped to the resort trail plan, with live chairlift movement and village landmarks.</p></CardHeader>
        <CardContent className="space-y-3"><div className="flex flex-wrap gap-2"><Badge className="bg-emerald-600 hover:bg-emerald-600">6 lifts operating</Badge><Badge variant="secondary">Trail map aligned</Badge></div><p className="flex items-center gap-2 text-xs text-muted-foreground"><Rotate3DIcon className="size-4" />Drag to explore, scroll or pinch to zoom.</p></CardContent>
      </Card>
      <div className="pointer-events-auto ml-auto hidden w-64 space-y-3 md:block">
        <GpsHud coordinate={inspectedCoordinate} />
        {selectedLandmark && <Card className="bg-white/92 shadow-lg backdrop-blur"><CardHeader className="pb-2"><CardTitle className="text-sm">{selectedLandmark.name}</CardTitle></CardHeader><CardContent><ResortLocationDetails landmark={selectedLandmark} /></CardContent></Card>}
        <Card className="bg-white/92 shadow-lg backdrop-blur"><CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><MountainSnowIcon className="size-4 text-sky-700" />Lift operations</CardTitle></CardHeader><CardContent className="space-y-1.5">{LIFTS.map((lift) => <Button key={lift.id} type="button" variant={activeLift === lift.id ? 'secondary' : 'ghost'} className="h-8 w-full justify-between text-xs" onClick={() => activeLift === lift.id ? setActiveLift(null) : focusLift(lift)}><span>{lift.name}</span><span className="size-2 rounded-full" style={{ backgroundColor: activeLift === lift.id ? '#facc15' : lift.color }} /></Button>)}</CardContent></Card>
        <details className="rounded-xl border border-white/60 bg-white/92 p-3 shadow-lg backdrop-blur"><summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-slate-800"><MapIcon className="size-4 text-sky-700" />Official trail map reference</summary><img src={TRAIL_MAP_URL} alt="Falls Creek resort trail map showing lifts, ski runs and village services" className="mt-3 aspect-[1.41] w-full rounded-md border object-cover" /></details>
      </div>
    </div>

    <PedestrianControls enabled={pedestriansEnabled} onToggle={setPedestriansEnabled} count={pedestrianCount} onSpawn={handleSpawnPedestrian} onReset={handleResetPedestrians} speed={pedestrianSpeed} onSpeedChange={setPedestrianSpeed} showPaths={showPedestrianPaths} onShowPathsChange={setShowPedestrianPaths} visibleRoles={visiblePedestrianRoles} onVisibleRolesChange={setVisiblePedestrianRoles} className="top-20" selectedPedestrian={selectedPedestrian} onDeselectPedestrian={() => setSelectedPedestrian(null)} />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 sm:p-6"><div className="pointer-events-auto flex w-fit items-center gap-2 rounded-full border border-white/70 bg-white/92 px-4 py-2 text-xs text-slate-700 shadow-lg backdrop-blur"><TreesIcon className="size-4 text-emerald-700" />Hover or select the terrain for GPS coordinates; double-click to focus the camera.</div></div>
    <div className="pointer-events-none absolute bottom-16 right-4 z-20 md:hidden"><GpsHud coordinate={inspectedCoordinate} /></div>
    {isMobile && selectedLandmark && <Sheet open onOpenChange={(open) => { if (!open) setSelectedLandmarkId(null); }}>
      <SheetContent side="bottom" className="max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]" onPointerDown={(event) => event.stopPropagation()}>
        <SheetHeader>
          <SheetTitle>{selectedLandmark.name}</SheetTitle>
          <SheetDescription>Resort location details and calibrated terrain position.</SheetDescription>
        </SheetHeader>
        <div className="mt-5"><ResortLocationDetails landmark={selectedLandmark} /></div>
      </SheetContent>
    </Sheet>}
  </div>;
}
